import React, { Fragment, useCallback, useMemo, useState } from 'react'
import { api } from '../api'
import { calculateDemoProductTotalCost, isSemiFinishedEligibleForRecipeReport, formatQuantity } from '../costUtils'
import {
  Category,
  ComponentItem,
  DemoComponentItem,
  DemoComponentType,
  DemoProduct,
  DemoSemiFinishedProduct,
  Packaging,
  Product,
  RawMaterial,
  SemiFinishedProduct,
  TradingGood,
} from '../types'
import { SearchableComponentSelect, SelectableComponentOption } from './SearchableComponentSelect'

interface Props {
  demoProducts: DemoProduct[]
  setDemoProducts?: React.Dispatch<React.SetStateAction<DemoProduct[]>>
  setProducts?: React.Dispatch<React.SetStateAction<Product[]>>
  demoSemiFinished?: DemoSemiFinishedProduct[]
  products: Product[]
  categories: Category[]
  rawMaterials: RawMaterial[]
  packagings: Packaging[]
  tradingGoods?: TradingGood[]
  semiFinished: SemiFinishedProduct[]
  onRefresh: () => Promise<void> | void
  onOpenReport?: (item: any, type: string) => void
}

function PieChartIcon({ size = 13, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}
    >
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      <path d="M22 12A10 10 0 0 0 12 2v10z" />
    </svg>
  )
}

function ChevronIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

function PencilIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  )
}

export function DemoProductsView({
  demoProducts,
  setDemoProducts,
  setProducts,
  demoSemiFinished = [],
  products,
  categories,
  rawMaterials,
  packagings,
  tradingGoods = [],
  semiFinished,
  onRefresh,
  onOpenReport,
}: Props) {
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingDemoProduct, setEditingDemoProduct] = useState<DemoProduct | null>(null)
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())

  // Component management modal
  const [activeProductForAddComponent, setActiveProductForAddComponent] = useState<DemoProduct | null>(null)
  const [editingCompQuantity, setEditingCompQuantity] = useState<{
    demoProduct: DemoProduct
    component: DemoComponentItem
    quantity: string
  } | null>(null)
  const [selectedCompType, setSelectedCompType] = useState<DemoComponentType>('raw_material')
  const [selectedCompId, setSelectedCompId] = useState('')
  const [compUnitAmount, setCompUnitAmount] = useState('1.0000')
  const [compUnitMode, setCompUnitMode] = useState<'kg' | 'g'>('kg')

  // "Gerçek Ortama Geçir" modal state
  const [convertingProduct, setConvertingProduct] = useState<DemoProduct | null>(null)
  const [convertCode, setConvertCode] = useState('')
  const [convertError, setConvertError] = useState<string | null>(null)
  const [convertLoading, setConvertLoading] = useState(false)
  const [showOverwritePrompt, setShowOverwritePrompt] = useState(false)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [error, setError] = useState<string | null>(null)

  const [localHiddenProductIds, setLocalHiddenProductIds] = useState<Set<string>>(new Set())
  const [localHiddenComponentIds, setLocalHiddenComponentIds] = useState<Set<string>>(new Set())

  // Lazy-loaded components cache
  const [lazyComponents, setLazyComponents] = useState<Record<string, DemoComponentItem[]>>({})

  // Maps for fast lookups
  const demoProductsMap = useMemo(() => {
    return new Map(
      demoProducts.map((p) => {
        if (lazyComponents[p.id]) {
          return [p.id, { ...p, components: lazyComponents[p.id] }]
        }
        return [p.id, p]
      })
    )
  }, [demoProducts, lazyComponents])
  const demoSemiFinishedMap = useMemo(() => new Map(demoSemiFinished.map((s) => [s.id, s])), [demoSemiFinished])
  const productsMap = useMemo(() => new Map(products.map((p) => [p.id, p])), [products])
  const semiFinishedMap = useMemo(() => new Map(semiFinished.map((s) => [s.id, s])), [semiFinished])
  const rawMaterialsMap = useMemo(() => new Map(rawMaterials.map((r) => [r.id, r])), [rawMaterials])
  const packagingsMap = useMemo(() => new Map(packagings.map((p) => [p.id, p])), [packagings])
  const tradingGoodsMap = useMemo(() => new Map((tradingGoods || []).map((t) => [t.id, t])), [tradingGoods])

  // Filtered demo products
  const filteredProducts = useMemo(() => {
    return demoProducts.filter((dp) => {
      if (localHiddenProductIds.has(dp.id)) return false
      const matchSearch = dp.name.toLowerCase().includes(searchTerm.toLowerCase())
      const matchCat = !selectedCategory || dp.category_id === selectedCategory
      return matchSearch && matchCat
    })
  }, [demoProducts, searchTerm, selectedCategory, localHiddenProductIds])

  const toggleExpand = async (key: string, dp?: DemoProduct) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

    if (
      dp &&
      (!dp.components || dp.components.length === 0) &&
      (dp.components_count ?? 0) > 0 &&
      !lazyComponents[dp.id]
    ) {
      try {
        const full = await api.getDemoProduct(dp.id)
        if (full && full.components) {
          setLazyComponents((prev) => ({ ...prev, [dp.id]: full.components || [] }))
        }
      } catch (err) {
        console.error('Failed to load components for demo product:', err)
      }
    }
  }

  // Handle Promotion to Production ("Gerçek Ortama Geçir")
  const handleOpenConvertModal = (dp: DemoProduct) => {
    setConvertingProduct(dp)
    setConvertCode('')
    setConvertError(null)
    setShowOverwritePrompt(false)
  }

  const handleCloseConvertModal = () => {
    setConvertingProduct(null)
    setConvertCode('')
    setConvertError(null)
    setConvertLoading(false)
    setShowOverwritePrompt(false)
  }

  const executeConvert = async (replaceExisting: boolean) => {
    if (!convertingProduct) return
    const cleanCode = convertCode.trim()
    setConvertLoading(true)
    setConvertError(null)
    try {
      const realProduct = await api.convertToRealProduct(convertingProduct.id, {
        code: cleanCode,
        replace_existing: replaceExisting,
      })
      if (setDemoProducts) {
        setDemoProducts((prev) => prev.filter((dp) => dp.id !== convertingProduct.id))
      }
      if (setProducts) {
        const cat = categories.find((c) => c.id === realProduct.category_id)
        const prodWithCat = { ...realProduct, category: cat || realProduct.category }
        setProducts((prev) => {
          if (prev.some((p) => p.id === realProduct.id)) {
            return prev.map((p) => (p.id === realProduct.id ? prodWithCat : p))
          }
          return [prodWithCat, ...prev]
        })
      }
      handleCloseConvertModal()
      if (!setDemoProducts && !setProducts) {
        await onRefresh()
      }
    } catch (err: any) {
      if (err.message && (err.message.includes('zaten kullanılmaktadır') || err.message.includes('409'))) {
        setShowOverwritePrompt(true)
      } else {
        setConvertError(err.message || 'Ürün gerçek ortama aktarılırken bir hata oluştu.')
      }
    } finally {
      setConvertLoading(false)
    }
  }

  const handleConvertSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!convertingProduct) return
    const cleanCode = convertCode.trim()
    if (!cleanCode) {
      setConvertError('Lütfen geçerli bir ürün kodu girin.')
      return
    }

    // Quick client-side check if code exists in products
    const codeExists = products.some((p) => p.code.toLowerCase() === cleanCode.toLowerCase())
    if (codeExists) {
      setShowOverwritePrompt(true)
      return
    }

    await executeConvert(false)
  }

  // Component options for SearchableComponentSelect
  const componentOptions: SelectableComponentOption[] = useMemo(() => {
    if (selectedCompType === 'demo_product') {
      return demoProducts
        .filter((dp) => activeProductForAddComponent && dp.id !== activeProductForAddComponent.id)
        .map((dp) => ({
          id: dp.id,
          code: 'DEMO',
          name: dp.name,
          unit: dp.unit,
          maliyet: Number(dp.maliyet || 0),
          typeLabel: 'Demo Mamul',
        }))
    } else if (selectedCompType === 'demo_semi_finished') {
      return demoSemiFinished.map((dsf) => ({
        id: dsf.id,
        code: 'DEMO',
        name: dsf.name,
        unit: dsf.unit,
        maliyet: Number(dsf.maliyet || 0),
        typeLabel: 'Demo Yarı Mamul',
      }))
    } else if (selectedCompType === 'product') {
      return products.map((p) => ({
        id: p.id,
        code: p.code,
        name: p.name,
        unit: p.unit,
        maliyet: Number(p.maliyet || 0),
        typeLabel: 'Mamul Ürün',
      }))
    } else if (selectedCompType === 'semi_finished') {
      return semiFinished.map((s) => ({
        id: s.id,
        code: s.code,
        name: s.name,
        unit: s.unit,
        maliyet: Number(s.maliyet || 0),
        typeLabel: 'Yarı Mamul',
      }))
    } else if (selectedCompType === 'raw_material') {
      return rawMaterials.map((r) => ({
        id: r.id,
        code: r.code,
        name: r.name,
        unit: r.unit,
        maliyet: Number(r.maliyet || 0),
        typeLabel: 'Hammadde',
      }))
    } else if (selectedCompType === 'packaging') {
      return packagings.map((pkg) => ({
        id: pkg.id,
        code: pkg.code,
        name: pkg.name,
        unit: pkg.unit,
        maliyet: Number(pkg.maliyet || 0),
        typeLabel: 'Ambalaj',
      }))
    } else if (selectedCompType === 'trading_good') {
      return (tradingGoods || []).map((tg) => ({
        id: tg.id,
        code: tg.code,
        name: tg.name,
        unit: tg.unit,
        maliyet: Number(tg.maliyet || 0),
        typeLabel: 'Al-Sat',
      }))
    }
    return []
  }, [selectedCompType, demoProducts, demoSemiFinished, products, semiFinished, rawMaterials, packagings, tradingGoods, activeProductForAddComponent])

  const selectedItemMeta = useMemo(() => {
    return componentOptions.find((opt) => opt.id === selectedCompId)
  }, [componentOptions, selectedCompId])

  const handleAddComponentSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!activeProductForAddComponent || !selectedCompId) return
    setError(null)

    const parentN = Number(activeProductForAddComponent.recipe_output_quantity || 1)
    let parsedAmount = parseFloat(compUnitAmount.replace(',', '.')) || 0
    if (compUnitMode === 'g' && selectedItemMeta?.unit === 'Kg') {
      parsedAmount = parsedAmount / 1000
    }
    const unitAmount = parentN > 0 ? parsedAmount / parentN : parsedAmount

    const payload: any = {
      component_type: selectedCompType,
      unit_amount: unitAmount,
      recipe_amount: parsedAmount,
    }

    if (selectedCompType === 'demo_product') payload.component_demo_product_id = selectedCompId
    else if (selectedCompType === 'demo_semi_finished') payload.component_demo_semi_finished_id = selectedCompId
    else if (selectedCompType === 'product') payload.component_product_id = selectedCompId
    else if (selectedCompType === 'semi_finished') payload.component_semi_finished_id = selectedCompId
    else if (selectedCompType === 'raw_material') payload.component_raw_material_id = selectedCompId
    else if (selectedCompType === 'packaging') payload.component_packaging_id = selectedCompId
    else if (selectedCompType === 'trading_good') payload.component_trading_good_id = selectedCompId

    try {
      const newComp = await api.addDemoComponent(activeProductForAddComponent.id, payload)
      const targetId = activeProductForAddComponent.id
      setSelectedCompId('')
      setCompUnitAmount('1.0000')
      setCompUnitMode('kg')
      setActiveProductForAddComponent(null)
      setExpandedKeys((prev) => new Set(prev).add(`dp_${targetId}`))
      if (setDemoProducts) {
        setDemoProducts((prev) =>
          prev.map((dp) => {
            if (dp.id !== targetId) return dp
            return {
              ...dp,
              components: [...(dp.components || []), newComp],
            }
          })
        )
      }
      if (lazyComponents[targetId]) {
        setLazyComponents((prev) => ({
          ...prev,
          [targetId]: [...(prev[targetId] || []), newComp],
        }))
      }
      if (!setDemoProducts) {
        await onRefresh()
      }
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleSaveComponentQuantity = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingCompQuantity) return
    const parentN = Number(editingCompQuantity.demoProduct.recipe_output_quantity || 1)
    const m = parseFloat(editingCompQuantity.quantity.replace(',', '.')) || 0
    const unitAmount = parentN > 0 ? m / parentN : m

    try {
      const updatedComp = await api.updateDemoComponent(
        editingCompQuantity.demoProduct.id,
        editingCompQuantity.component.id,
        {
          recipe_amount: m,
          unit_amount: unitAmount,
        }
      )
      const targetId = editingCompQuantity.demoProduct.id
      const compId = editingCompQuantity.component.id
      setEditingCompQuantity(null)
      if (setDemoProducts) {
        setDemoProducts((prev) =>
          prev.map((dp) => {
            if (dp.id !== targetId) return dp
            return {
              ...dp,
              components: (dp.components || []).map((c) =>
                c.id === compId
                  ? {
                      ...c,
                      ...updatedComp,
                      recipe_amount: m,
                      unit_amount: unitAmount,
                    }
                  : c
              ),
            }
          })
        )
      }
      if (lazyComponents[targetId]) {
        setLazyComponents((prev) => ({
          ...prev,
          [targetId]: (prev[targetId] || []).map((c) =>
            c.id === compId
              ? {
                  ...c,
                  ...updatedComp,
                  recipe_amount: m,
                  unit_amount: unitAmount,
                }
              : c
          ),
        }))
      }
      if (!setDemoProducts) {
        await onRefresh()
      }
    } catch (err: any) {
      alert(err.message)
    }
  }

  const handleDeleteComponent = async (demoProductId: string, componentId: string) => {
    if (!confirm('Bu bileşeni kaldırmak istediğinizden emin misiniz?')) return
    const parentProduct = demoProducts.find((dp) => dp.id === demoProductId)
    const compToDelete = parentProduct?.components?.find((c) => c.id === componentId)
    const originalIndex = parentProduct?.components?.findIndex((c) => c.id === componentId) ?? -1
    const prevLazyComps = lazyComponents[demoProductId]

    if (setDemoProducts) {
      setDemoProducts((prev) =>
        prev.map((dp) => {
          if (dp.id !== demoProductId) return dp
          return {
            ...dp,
            components: (dp.components || []).filter((c) => c.id !== componentId),
          }
        })
      )
    }
    if (lazyComponents[demoProductId]) {
      setLazyComponents((prev) => ({
        ...prev,
        [demoProductId]: (prev[demoProductId] || []).filter((c) => c.id !== componentId),
      }))
    }
    setLocalHiddenComponentIds((prev) => new Set(prev).add(componentId))

    try {
      await api.deleteDemoComponent(demoProductId, componentId)
    } catch (err: any) {
      if (setDemoProducts && compToDelete && originalIndex >= 0) {
        setDemoProducts((prev) =>
          prev.map((dp) => {
            if (dp.id !== demoProductId) return dp
            const comps = [...(dp.components || [])]
            if (!comps.some((c) => c.id === componentId)) {
              comps.splice(originalIndex, 0, compToDelete)
            }
            return { ...dp, components: comps }
          })
        )
      }
      if (prevLazyComps) {
        setLazyComponents((prev) => ({
          ...prev,
          [demoProductId]: prevLazyComps,
        }))
      }
      setLocalHiddenComponentIds((prev) => {
        const next = new Set(prev)
        next.delete(componentId)
        return next
      })
      alert(err.message)
      return
    }

    setLocalHiddenComponentIds((prev) => {
      const next = new Set(prev)
      next.delete(componentId)
      return next
    })
  }

  const handleDeleteDemoProduct = async (id: string) => {
    if (!confirm('Bu demo mamul ürünü silmek istediğinizden emin misiniz?')) return
    const itemToDelete = demoProducts.find((dp) => dp.id === id)
    const originalIndex = demoProducts.findIndex((dp) => dp.id === id)

    if (setDemoProducts) {
      setDemoProducts((prev) => prev.filter((item) => item.id !== id))
    }
    setLocalHiddenProductIds((prev) => new Set(prev).add(id))

    try {
      await api.deleteDemoProduct(id)
    } catch (err: any) {
      if (setDemoProducts && itemToDelete) {
        setDemoProducts((prev) => {
          if (prev.some((dp) => dp.id === id)) return prev
          const next = [...prev]
          next.splice(originalIndex >= 0 ? originalIndex : 0, 0, itemToDelete)
          return next
        })
      }
      setLocalHiddenProductIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      alert(err.message)
      return
    }

    setLocalHiddenProductIds((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'demo_product':
        return <span className="badge badge-secondary" style={{ backgroundColor: '#6366f1', color: '#fff' }}>Demo Mamul</span>
      case 'demo_semi_finished':
        return <span className="badge badge-secondary" style={{ backgroundColor: '#8b5cf6', color: '#fff' }}>Demo Yarı Mamul</span>
      case 'raw_material':
        return <span className="badge badge-info">Hammadde</span>
      case 'packaging':
        return <span className="badge badge-warning">Ambalaj</span>
      case 'trading_good':
        return <span className="badge badge-purple" style={{ backgroundColor: '#6f42c1', color: '#fff' }}>Al-Sat</span>
      case 'semi_finished':
        return <span className="badge badge-primary">Yarı Mamul</span>
      case 'product':
        return <span className="badge badge-success">Mamul Ürün</span>
      default:
        return <span className="badge badge-secondary">{type}</span>
    }
  }

  // Recursively compute the unit cost of a component including all its sub-components
  const getRecursiveUnitCost = useCallback(
    (
      componentType: DemoComponentType,
      componentId: string,
      baseCost: number,
      visited: Set<string> = new Set()
    ): number => {
      if (visited.has(componentId)) {
        return baseCost
      }

      if (componentType === 'raw_material') {
        const rm = rawMaterialsMap.get(componentId)
        return Number(rm?.maliyet ?? baseCost)
      }
      if (componentType === 'packaging') {
        const pkg = packagingsMap.get(componentId)
        return Number(pkg?.maliyet ?? baseCost)
      }
      if (componentType === 'trading_good') {
        const tg = tradingGoodsMap.get(componentId)
        return Number(tg?.maliyet ?? baseCost)
      }

      const nextVisited = new Set(visited)
      nextVisited.add(componentId)

      let subCost = 0

      if (componentType === 'demo_product') {
        const dp = demoProductsMap.get(componentId)
        const directBase = Number(baseCost || dp?.maliyet || 0)
        const subComps = dp?.components || []
        for (const child of subComps) {
          const childQty = Number(child.unit_amount || 0)
          const childTargetId =
            child.component_demo_product_id ||
            child.component_demo_semi_finished_id ||
            child.component_product_id ||
            child.component_semi_finished_id ||
            child.component_raw_material_id ||
            child.component_packaging_id ||
            child.component_trading_good_id ||
            ''
          const childBase = Number(
            child.component_cost ??
              (child.component_type === 'demo_product'
                ? demoProductsMap.get(childTargetId)?.maliyet
                : child.component_type === 'demo_semi_finished'
                ? demoSemiFinishedMap.get(childTargetId)?.maliyet
                : child.component_type === 'product'
                ? productsMap.get(childTargetId)?.maliyet
                : child.component_type === 'semi_finished'
                ? semiFinishedMap.get(childTargetId)?.maliyet
                : child.component_type === 'raw_material'
                ? rawMaterialsMap.get(childTargetId)?.maliyet
                : child.component_type === 'packaging'
                ? packagingsMap.get(childTargetId)?.maliyet
                : child.component_type === 'trading_good'
                ? tradingGoodsMap.get(childTargetId)?.maliyet
                : 0) ??
              0
          )
          const childUnitCost = getRecursiveUnitCost(
            child.component_type,
            childTargetId,
            childBase,
            nextVisited
          )
          subCost += childQty * childUnitCost
        }
        return directBase + subCost
      }

      if (componentType === 'demo_semi_finished') {
        const dsf = demoSemiFinishedMap.get(componentId)
        const directBase = Number(baseCost || dsf?.maliyet || 0)
        const subComps = dsf?.components || []
        for (const child of subComps) {
          const childQty = Number(child.unit_amount || 0)
          const childTargetId =
            child.component_demo_semi_finished_id ||
            child.component_semi_finished_id ||
            child.component_raw_material_id ||
            child.component_packaging_id ||
            child.component_trading_good_id ||
            ''
          const childBase = Number(
            child.component_cost ??
              (child.component_type === 'demo_semi_finished'
                ? demoSemiFinishedMap.get(childTargetId)?.maliyet
                : child.component_type === 'semi_finished'
                ? semiFinishedMap.get(childTargetId)?.maliyet
                : child.component_type === 'raw_material'
                ? rawMaterialsMap.get(childTargetId)?.maliyet
                : child.component_type === 'packaging'
                ? packagingsMap.get(childTargetId)?.maliyet
                : child.component_type === 'trading_good'
                ? tradingGoodsMap.get(childTargetId)?.maliyet
                : 0) ??
              0
          )
          const childUnitCost = getRecursiveUnitCost(
            child.component_type,
            childTargetId,
            childBase,
            nextVisited
          )
          subCost += childQty * childUnitCost
        }
        return directBase + subCost
      }

      if (componentType === 'semi_finished') {
        const semi = semiFinishedMap.get(componentId)
        const directBase = Number(baseCost || semi?.maliyet || 0)
        const subComps = semi?.components || []
        for (const child of subComps) {
          const childQty = Number(child.unit_amount || child.quantity || 0)
          const childBase = Number(child.birim_maliyet || 0)
          const childUnitCost = getRecursiveUnitCost(
            child.component_type,
            child.component_id,
            childBase,
            nextVisited
          )
          subCost += childQty * childUnitCost
        }
        return directBase + subCost
      }

      if (componentType === 'product') {
        const prod = productsMap.get(componentId)
        const directBase = Number(baseCost || prod?.maliyet || 0)
        const subComps = prod?.components || []
        for (const child of subComps) {
          const childQty = Number(child.unit_amount || child.quantity || 0)
          const childBase = Number(child.birim_maliyet || 0)
          const childUnitCost = getRecursiveUnitCost(
            child.component_type,
            child.component_id,
            childBase,
            nextVisited
          )
          subCost += childQty * childUnitCost
        }
        return directBase + subCost
      }

      return baseCost
    },
    [demoProductsMap, demoSemiFinishedMap, productsMap, semiFinishedMap, rawMaterialsMap, packagingsMap, tradingGoodsMap]
  )

  const renderComponentTree = (
    rootProduct: DemoProduct,
    components: (DemoComponentItem | ComponentItem)[],
    depth: number,
    parentPath: string,
    ancestorIds: Set<string>
  ): React.ReactNode[] => {
    return components
      .filter((comp) => !localHiddenComponentIds.has(comp.id))
      .map((comp) => {
      const childPath = `${parentPath}/${comp.id}`
      const isNode =
        comp.component_type === 'demo_product' ||
        comp.component_type === 'demo_semi_finished' ||
        comp.component_type === 'semi_finished' ||
        comp.component_type === 'product'
      const isExpanded = expandedKeys.has(childPath)
      const isDirectChild = depth === 1

      const targetEntityId =
        ('component_id' in comp ? comp.component_id : null) ||
        ('component_demo_product_id' in comp ? comp.component_demo_product_id : null) ||
        ('component_demo_semi_finished_id' in comp ? comp.component_demo_semi_finished_id : null) ||
        ('component_product_id' in comp ? comp.component_product_id : null) ||
        ('component_semi_finished_id' in comp ? comp.component_semi_finished_id : null) ||
        ('component_raw_material_id' in comp ? comp.component_raw_material_id : null) ||
        ('component_packaging_id' in comp ? comp.component_packaging_id : null) ||
        ('component_trading_good_id' in comp ? (comp as any).component_trading_good_id : null) ||
        ''

      const unitAmountNum = Number(
        ('unit_amount' in comp ? comp.unit_amount : null) ??
          ('quantity' in comp ? (comp as any).quantity : null) ??
          1
      )

      const baseCostNum = Number(
        ('component_cost' in comp ? comp.component_cost : null) ??
          ('birim_maliyet' in comp ? (comp as any).birim_maliyet : null) ??
          (comp.component_type === 'demo_product'
            ? demoProductsMap.get(targetEntityId)?.maliyet
            : comp.component_type === 'demo_semi_finished'
            ? demoSemiFinishedMap.get(targetEntityId)?.maliyet
            : comp.component_type === 'product'
            ? productsMap.get(targetEntityId)?.maliyet
            : comp.component_type === 'semi_finished'
            ? semiFinishedMap.get(targetEntityId)?.maliyet
            : comp.component_type === 'raw_material'
            ? rawMaterialsMap.get(targetEntityId)?.maliyet
            : comp.component_type === 'packaging'
            ? packagingsMap.get(targetEntityId)?.maliyet
            : comp.component_type === 'trading_good'
            ? tradingGoodsMap.get(targetEntityId)?.maliyet
            : 0) ??
          0
      )

      const name =
        ('component_name' in comp ? comp.component_name : null) ||
        ('name' in comp ? (comp as any).name : null) ||
        (comp.component_type === 'demo_product' ? demoProductsMap.get(targetEntityId)?.name : null) ||
        (comp.component_type === 'demo_semi_finished' ? demoSemiFinishedMap.get(targetEntityId)?.name : null) ||
        (comp.component_type === 'product' ? productsMap.get(targetEntityId)?.name : null) ||
        (comp.component_type === 'semi_finished' ? semiFinishedMap.get(targetEntityId)?.name : null) ||
        (comp.component_type === 'raw_material' ? rawMaterialsMap.get(targetEntityId)?.name : null) ||
        (comp.component_type === 'packaging' ? packagingsMap.get(targetEntityId)?.name : null) ||
        (comp.component_type === 'trading_good' ? tradingGoodsMap.get(targetEntityId)?.name : null) ||
        'Bileşen'

      const code =
        ('component_code' in comp ? comp.component_code : null) ||
        ('code' in comp ? (comp as any).code : null) ||
        (comp.component_type === 'product' ? productsMap.get(targetEntityId)?.code : null) ||
        (comp.component_type === 'semi_finished' ? semiFinishedMap.get(targetEntityId)?.code : null) ||
        (comp.component_type === 'raw_material' ? rawMaterialsMap.get(targetEntityId)?.code : null) ||
        (comp.component_type === 'packaging' ? packagingsMap.get(targetEntityId)?.code : null) ||
        (comp.component_type === 'trading_good' ? tradingGoodsMap.get(targetEntityId)?.code : null) ||
        (comp.component_type === 'demo_product' ? 'DEMO' : null) ||
        (comp.component_type === 'demo_semi_finished' ? 'DEMO' : null)

      const unit =
        ('component_unit' in comp ? comp.component_unit : null) ||
        ('unit' in comp ? (comp as any).unit : null) ||
        (comp.component_type === 'demo_product' ? demoProductsMap.get(targetEntityId)?.unit || 'Adet' : null) ||
        (comp.component_type === 'demo_semi_finished' ? demoSemiFinishedMap.get(targetEntityId)?.unit || 'Adet' : null) ||
        (comp.component_type === 'product' ? productsMap.get(targetEntityId)?.unit || 'Adet' : null) ||
        (comp.component_type === 'semi_finished' ? semiFinishedMap.get(targetEntityId)?.unit || 'Adet' : null) ||
        (comp.component_type === 'raw_material' ? rawMaterialsMap.get(targetEntityId)?.unit || 'Adet' : null) ||
        (comp.component_type === 'packaging' ? packagingsMap.get(targetEntityId)?.unit || 'Adet' : null) ||
        (comp.component_type === 'trading_good' ? tradingGoodsMap.get(targetEntityId)?.unit || 'Adet' : null) ||
        'Adet'

      const recursiveUnitCost = getRecursiveUnitCost(
        comp.component_type,
        targetEntityId,
        baseCostNum,
        ancestorIds
      )
      const totalContribution = unitAmountNum * recursiveUnitCost

      // Sub-components & cycle detection
      let subComponents: (DemoComponentItem | ComponentItem)[] = []
      let hasCycle = false
      if (isNode) {
        if (ancestorIds.has(targetEntityId)) {
          hasCycle = true
        } else if (comp.component_type === 'demo_product') {
          subComponents = demoProductsMap.get(targetEntityId)?.components || []
        } else if (comp.component_type === 'demo_semi_finished') {
          subComponents = demoSemiFinishedMap.get(targetEntityId)?.components || []
        } else if (comp.component_type === 'product') {
          subComponents = productsMap.get(targetEntityId)?.components || []
        } else if (comp.component_type === 'semi_finished') {
          subComponents = semiFinishedMap.get(targetEntityId)?.components || []
        }
      }

      return (
        <div key={childPath} className="bom-tree-item-wrapper">
          <div
            className={`bom-tree-row bom-tree-depth-${Math.min(depth, 3)}`}
            style={{ paddingLeft: `${(depth - 1) * 24 + 10}px` }}
          >
            <div className="bom-row-left">
              {isNode ? (
                <button
                  type="button"
                  className={`tree-toggle-btn ${isExpanded ? 'expanded' : ''}`}
                  aria-label={
                    isExpanded
                      ? `${name} alt bileşenlerini daralt`
                      : `${name} alt bileşenlerini genişlet`
                  }
                  aria-expanded={isExpanded}
                  onClick={() => toggleExpand(childPath)}
                >
                  <ChevronIcon />
                </button>
              ) : (
                <span className="tree-leaf-spacer" aria-hidden="true" />
              )}
              {getTypeBadge(comp.component_type)}
              {code && <code className="bom-code-badge">{code}</code>}
              <span className="bom-item-name" style={{ fontWeight: isDirectChild ? 600 : 400 }}>
                {name}
              </span>
              {(() => {
                let sf: any = null
                let sType = ''
                if (comp.component_type === 'demo_semi_finished') {
                  sf = demoSemiFinishedMap.get(targetEntityId)
                  sType = 'demo_semi_finished'
                } else if (comp.component_type === 'semi_finished') {
                  sf = semiFinishedMap.get(targetEntityId)
                  sType = 'semi_finished'
                }
                if (!sf || !isSemiFinishedEligibleForRecipeReport(sf)) return null
                return (
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpenReport?.(sf, sType)
                    }}
                    title="Yarı Mamul Reçete Raporu"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '2px 4px',
                      marginLeft: '6px',
                      lineHeight: 1,
                      verticalAlign: 'middle',
                    }}
                  >
                    <PieChartIcon size={12} style={{ marginRight: 0 }} />
                  </button>
                )
              })()}
            </div>

            <div className="bom-row-right">
              <div className="bom-pill-metric">
                <span className="bom-pill-label">Birim Miktar:</span>
                <span className="tabular-nums bom-pill-value">
                  {formatQuantity(unitAmountNum)} {unit}
                </span>
                {isDirectChild && (
                  <button
                    type="button"
                    className="cell-edit-btn"
                    aria-label={`${name} birim miktarını düzenle`}
                    onClick={() => {
                      const parentN = Number(rootProduct.recipe_output_quantity || 1)
                      const mVal = comp.recipe_amount != null ? Number(comp.recipe_amount) : unitAmountNum * parentN
                      setEditingCompQuantity({
                        demoProduct: rootProduct,
                        component: comp as DemoComponentItem,
                        quantity: String(mVal),
                      })
                    }}
                  >
                    <PencilIcon />
                  </button>
                )}
              </div>

              <div className="bom-pill-metric">
                <span className="bom-pill-label">
                  {isNode ? 'Taban Maliyet:' : 'Birim Maliyet:'}
                </span>
                <span className="tabular-nums bom-pill-value">₺{baseCostNum.toFixed(2)}</span>
              </div>

              <div className="bom-pill-metric">
                <span className="bom-pill-label">Toplam Maliyet:</span>
                <span
                  className="tabular-nums bom-pill-value"
                  style={{ color: 'var(--color-primary)', fontWeight: 600 }}
                >
                  ₺{totalContribution.toFixed(2)}
                </span>
              </div>

              <div className="bom-row-actions">
                {isDirectChild ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                    aria-label={`${name} bileşenini kaldır`}
                    onClick={() => handleDeleteComponent(rootProduct.id, comp.id)}
                  >
                    ✕ Kaldır
                  </button>
                ) : (
                  <span
                    className="text-muted"
                    style={{ fontSize: '0.75rem', width: '50px', textAlign: 'center', display: 'inline-block' }}
                    title="Alt bileşenlerin reçetesi kendi menüsünden yönetilir"
                  >
                    —
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Expanded Sub-components */}
          {isNode && isExpanded && (
            <div className="bom-sub-tree">
              {hasCycle ? (
                <div
                  className="bom-tree-alert"
                  style={{ paddingLeft: `${depth * 24 + 34}px` }}
                >
                  ⚠️ Döngüsel referans algılandı ({name})
                </div>
              ) : subComponents.length === 0 ? (
                <div
                  className="bom-tree-empty-hint"
                  style={{ paddingLeft: `${depth * 24 + 34}px` }}
                >
                  Bu alt bileşenin tanımlı bir reçetesi bulunmuyor.
                </div>
              ) : (
                renderComponentTree(
                  rootProduct,
                  subComponents,
                  depth + 1,
                  childPath,
                  new Set([...ancestorIds, targetEntityId])
                )
              )}
            </div>
          )}
        </div>
      )
    })
  }

  // Check if convertingProduct has child demo products or demo semi-finished products
  const hasSubDemoComponents = useMemo(() => {
    if (!convertingProduct || !convertingProduct.components) return false
    return convertingProduct.components.some(
      (c) => c.component_type === 'demo_product' || c.component_type === 'demo_semi_finished'
    )
  }, [convertingProduct])

  return (
    <div className="view-container">
      <div className="section-header-flex">
        <div>
          <h2>Demo Mamul Ürünler</h2>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
          + Yeni Demo Ürün Ekle
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Filter Bar */}
      <div className="filter-bar" style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          className="form-control"
          placeholder="Demo ürün adına göre ara…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '300px' }}
        />
        <select
          className="form-control"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{ maxWidth: '220px' }}
        >
          <option value="">Tüm Kategoriler</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Data Table */}
      <div className="table-card">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '38px' }}></th>
              <th>Demo Ürün Adı</th>
              <th>Kategori</th>
              <th>Birim</th>
              <th>KDV Oranı</th>
              <th style={{ textAlign: 'right' }}>Taban Maliyet</th>
              <th style={{ textAlign: 'right' }}>Bileşenler</th>
              <th style={{ textAlign: 'right' }}>Toplam Maliyet (Alış)</th>
              <th style={{ width: '210px', textAlign: 'center' }}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center empty-state" style={{ padding: '3rem 1rem' }}>
                  <div style={{ color: 'var(--text-muted, #6b7280)', marginBottom: '0.75rem' }}>
                    Henüz tanımlı bir demo mamul ürün bulunmamaktadır.
                  </div>
                  <button className="btn btn-sm btn-primary" onClick={() => setShowAddModal(true)}>
                    + İlk Demo Ürünü Ekle
                  </button>
                </td>
              </tr>
            ) : (
              filteredProducts.map((dp) => {
                const rootKey = `dp_${dp.id}`
                const isExpanded = expandedKeys.has(rootKey)
                const comps = (lazyComponents[dp.id] || dp.components || []).filter((c) => !localHiddenComponentIds.has(c.id))
                const totalCost = calculateDemoProductTotalCost(
                  dp,
                  demoProductsMap,
                  demoSemiFinishedMap,
                  productsMap,
                  semiFinishedMap,
                  rawMaterialsMap,
                  packagingsMap,
                  tradingGoodsMap
                )
                const baseCost = Number(dp.maliyet || 0)
                const componentsCost = Math.max(0, totalCost - baseCost)

                return (
                  <Fragment key={dp.id}>
                    <tr style={{ height: '76px' }}>
                      <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '0.75rem 0.5rem' }}>
                        <button
                          type="button"
                          className={`tree-toggle-btn ${isExpanded ? 'expanded' : ''}`}
                          aria-label={
                            isExpanded
                              ? `${dp.name} bileşen ağacını daralt`
                              : `${dp.name} bileşen ağacını genişlet`
                          }
                          aria-expanded={isExpanded}
                          onClick={() => toggleExpand(rootKey, dp)}
                        >
                          <ChevronIcon />
                        </button>
                      </td>
                      <td style={{ verticalAlign: 'middle', padding: '0.75rem 0.5rem' }}>
                        <strong>{dp.name}</strong>
                      </td>
                      <td className="text-muted" style={{ verticalAlign: 'middle', padding: '0.75rem 0.5rem' }}>
                        {dp.category_name || categories.find((c) => c.id === dp.category_id)?.name || '—'}
                      </td>
                      <td style={{ verticalAlign: 'middle', padding: '0.75rem 0.5rem' }}>{dp.unit || 'Adet'}</td>
                      <td style={{ verticalAlign: 'middle', padding: '0.75rem 0.5rem' }}>%{Math.round(Number(dp.kdv_rate || 0.2) * 100)}</td>
                      <td style={{ textAlign: 'right', verticalAlign: 'middle', padding: '0.75rem 0.5rem' }} className="tabular-nums">
                        {baseCost.toFixed(2)} TL
                      </td>
                      <td style={{ textAlign: 'right', verticalAlign: 'middle', padding: '0.75rem 0.5rem' }} className="tabular-nums">
                        {componentsCost > 0 ? `${componentsCost.toFixed(2)} TL` : '—'}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, verticalAlign: 'middle', padding: '0.75rem 0.5rem' }} className="tabular-nums">
                        {totalCost.toFixed(2)} TL
                      </td>
                      <td style={{ verticalAlign: 'middle', padding: '0.75rem 0.5rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', maxWidth: '200px', margin: '0 auto' }}>
                          <div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'center' }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.45rem', flex: 1, whiteSpace: 'nowrap' }}
                              onClick={() => setActiveProductForAddComponent(dp)}
                              title="Bileşen Ekle"
                            >
                              + Bileşen
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.45rem', flex: 1, whiteSpace: 'nowrap' }}
                              onClick={() => setEditingDemoProduct(dp)}
                            >
                              Düzenle
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.45rem' }}
                              onClick={() => handleDeleteDemoProduct(dp.id)}
                            >
                              Sil
                            </button>
                          </div>

                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '0.75rem', padding: '0.3rem 0.5rem', width: '100%', whiteSpace: 'nowrap' }}
                            onClick={() => handleOpenConvertModal(dp)}
                            title="Bu demo ürünü gerçek mamul ürün kataloğuna aktar"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                            <span>Gerçek Ortama Geçir</span>
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Cascading Tree Container */}
                    {isExpanded && (
                      <tr className="bom-expansion-row">
                        <td colSpan={9} className="bom-expansion-cell">
                          <div className="bom-dropdown-container">
                            {comps.length === 0 ? (
                              <div className="bom-empty-state">
                                <span>Bu demo ürüne henüz bir bileşen eklenmemiş.</span>
                                <button
                                  type="button"
                                  className="tree-add-btn"
                                  onClick={() => setActiveProductForAddComponent(dp)}
                                >
                                  + İlk Bileşeni Ekle
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="bom-tree-list">
                                  {renderComponentTree(dp, comps, 1, rootKey, new Set([dp.id]))}
                                </div>
                                <div className="bom-tree-add-footer">
                                  <button
                                    type="button"
                                    className="tree-add-btn"
                                    onClick={() => setActiveProductForAddComponent(dp)}
                                  >
                                    + <strong>{dp.name}</strong> İçin Yeni Bileşen Ekle
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal: Gerçek Ortama Geçir */}
      {convertingProduct && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '500px', width: '90%' }}>
            <h3>Gerçek Ortama Geçir</h3>
            <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
              <strong>{convertingProduct.name}</strong> adlı demo mamul ürün gerçek üretim kataloğuna aktarılacaktır.
            </p>

            {hasSubDemoComponents && (
              <div className="alert alert-danger" style={{ marginBottom: '1.25rem', fontSize: '0.85rem' }}>
                <strong>Dikkat:</strong> Bu demo ürünün alt bileşenleri arasında başka bir demo mamul veya demo yarı mamul ürün bulunmaktadır. Gerçek mamul ürünler demo ürün içeremez. Lütfen önce alt demo ürünleri gerçek ortama aktarın veya bileşenlerden çıkarın.
              </div>
            )}

            {convertError && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{convertError}</div>}

            {showOverwritePrompt ? (
              <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '6px', padding: '1rem', marginBottom: '1rem' }}>
                <h4 style={{ color: '#b45309', margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>Kod Zaten Kullanımda</h4>
                <p style={{ fontSize: '0.85rem', color: '#92400e', marginBottom: '1rem' }}>
                  <strong>'{convertCode}'</strong> ürün kodu zaten kullanılmaktadır. Mevcut kaydın üzerine yazmak istiyor musunuz?
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowOverwritePrompt(false)}
                    disabled={convertLoading}
                  >
                    Vazgeç
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={() => executeConvert(true)}
                    disabled={convertLoading}
                  >
                    {convertLoading ? 'İşleniyor…' : 'Evet, Üzerine Yaz'}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleConvertSubmit}>
                <div className="form-group">
                  <label>Yeni Ürün Kodu *</label>
                  <input
                    required
                    autoFocus
                    placeholder="Örn: PRD-1001, ELK-MML-05"
                    value={convertCode}
                    onChange={(e) => setConvertCode(e.target.value)}
                    disabled={hasSubDemoComponents || convertLoading}
                  />
                  <span className="helper-text">
                    Katalogda benzersiz bir ürün kodu belirtin.
                  </span>
                </div>

                <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseConvertModal}
                    disabled={convertLoading}
                  >
                    Vazgeç
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={hasSubDemoComponents || convertLoading}
                  >
                    {convertLoading ? 'Aktarılıyor...' : 'Gerçek Ortama Aktar'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Modal: Add Demo Product */}
      {showAddModal && (
        <DemoProductFormModal
          categories={categories}
          onClose={() => setShowAddModal(false)}
          onSuccess={async (created) => {
            setShowAddModal(false)
            if (setDemoProducts && created) {
              setDemoProducts((prev) => [created, ...prev])
            } else {
              await onRefresh()
            }
          }}
        />
      )}

      {/* Modal: Edit Demo Product */}
      {editingDemoProduct && (
        <DemoProductFormModal
          initialProduct={editingDemoProduct}
          categories={categories}
          onClose={() => setEditingDemoProduct(null)}
          onSuccess={async (updated) => {
            setEditingDemoProduct(null)
            if (setDemoProducts && updated) {
              setDemoProducts((prev) => prev.map((dp) => (dp.id === updated.id ? { ...dp, ...updated } : dp)))
            } else {
              await onRefresh()
            }
          }}
        />
      )}

      {/* Modal: Add Component to Demo Product */}
      {activeProductForAddComponent && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '520px', width: '90%' }}>
            <h3>Bileşen Ekle: {activeProductForAddComponent.name}</h3>
            <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
              Demo mamul ürünün reçetesine hammadde, ambalaj, yarı mamul, mamul veya başka bir demo ürün ekleyin.
            </p>

            <form onSubmit={handleAddComponentSubmit}>
              <div className="form-group">
                <label>Bileşen Türü *</label>
                <select
                  value={selectedCompType}
                  onChange={(e) => {
                    setSelectedCompType(e.target.value as DemoComponentType)
                    setSelectedCompId('')
                  }}
                >
                  <option value="raw_material">Hammadde</option>
                  <option value="packaging">Ambalaj</option>
                  <option value="trading_good">Al-Sat Ürün</option>
                  <option value="semi_finished">Yarı Mamul</option>
                  <option value="product">Mamul Ürün</option>
                  <option value="demo_product">Demo Mamul Ürün</option>
                  <option value="demo_semi_finished">Demo Yarı Mamul</option>
                </select>
              </div>

              <div className="form-group">
                <label>Bileşen Seçin *</label>
                <SearchableComponentSelect
                  options={componentOptions}
                  value={selectedCompId}
                  onChange={(id) => setSelectedCompId(id)}
                  placeholder="Seçmek için arayın…"
                />
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <label>
                    {Number(activeProductForAddComponent.recipe_output_quantity || 1)} {activeProductForAddComponent.unit || 'Adet'} Başına Miktar *
                  </label>
                  {selectedItemMeta?.unit === 'Kg' && (
                    <div className="btn-group">
                      <button
                        type="button"
                        className={`btn btn-xs ${compUnitMode === 'kg' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setCompUnitMode('kg')}
                      >
                        Kg
                      </button>
                      <button
                        type="button"
                        className={`btn btn-xs ${compUnitMode === 'g' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setCompUnitMode('g')}
                      >
                        g
                      </button>
                    </div>
                  )}
                </div>
                <input
                  type="number"
                  step="0.000001"
                  min="0.000001"
                  required
                  placeholder="1.000000"
                  value={compUnitAmount}
                  onChange={(e) => setCompUnitAmount(e.target.value)}
                />
                <span className="helper-text">
                  Birim: {compUnitMode === 'g' && selectedItemMeta?.unit === 'Kg' ? 'Gram (g)' : selectedItemMeta?.unit || 'Birim'}
                </span>
              </div>

              <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setActiveProductForAddComponent(null)}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={!selectedCompId}>
                  Bileşeni Ekle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Edit Component Quantity */}
      {editingCompQuantity && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '440px', width: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Birim Başına Miktar Düzenle</h3>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                style={{ padding: '0.2rem 0.5rem', lineHeight: 1 }}
                aria-label="Kapat"
                onClick={() => setEditingCompQuantity(null)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSaveComponentQuantity}>
              <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
                <strong>{editingCompQuantity.demoProduct.name}</strong> için girdi olan{' '}
                <strong>{editingCompQuantity.component.component_name || 'Bileşen'}</strong>
                {editingCompQuantity.component.component_code && ` (${editingCompQuantity.component.component_code})`}
              </p>
              <div className="form-group">
                <label htmlFor="edit-comp-quantity">
                  {Number(editingCompQuantity.demoProduct.recipe_output_quantity || 1)} {editingCompQuantity.demoProduct.unit || 'Adet'} Başına Miktar ({editingCompQuantity.component.component_unit || 'Adet'})
                </label>
                <input
                  id="edit-comp-quantity"
                  name="quantity"
                  type="number"
                  step="0.000001"
                  min="0.000001"
                  required
                  autoComplete="off"
                  value={editingCompQuantity.quantity}
                  onChange={(e) =>
                    setEditingCompQuantity({
                      ...editingCompQuantity,
                      quantity: e.target.value,
                    })
                  }
                />
                <span className="helper-text" style={{ fontSize: '0.8rem', marginTop: '0.25rem', display: 'block', color: 'var(--color-text-muted)' }}>
                  Birim miktar (m/n): {(parseFloat(editingCompQuantity.quantity) ? formatQuantity(parseFloat(editingCompQuantity.quantity) / Number(editingCompQuantity.demoProduct.recipe_output_quantity || 1)) : '0')} {editingCompQuantity.component.component_unit || 'Adet'}
                </span>
              </div>
              <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingCompQuantity(null)}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

interface FormModalProps {
  initialProduct?: DemoProduct
  categories: Category[]
  onClose: () => void
  onSuccess: (saved?: DemoProduct) => Promise<void> | void
}

function DemoProductFormModal({ initialProduct, categories, onClose, onSuccess }: FormModalProps) {
  const [name, setName] = useState(initialProduct?.name || '')
  const [categoryId, setCategoryId] = useState(initialProduct?.category_id || '')
  const [unit, setUnit] = useState(initialProduct?.unit || 'Adet')
  const [recipeOutputQuantity, setRecipeOutputQuantity] = useState(String(initialProduct?.recipe_output_quantity || '1'))
  const [kdvRate, setKdvRate] = useState(String(Number(initialProduct?.kdv_rate ?? 0.2) * 100))
  const [maliyet, setMaliyet] = useState(String(initialProduct?.maliyet ?? '0.00'))
  const [description, setDescription] = useState(initialProduct?.description || '')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const payload: Partial<DemoProduct> = {
      name: name.trim(),
      category_id: categoryId || null,
      unit,
      recipe_output_quantity: parseFloat(recipeOutputQuantity) || 1.0,
      kdv_rate: (parseFloat(kdvRate) || 20) / 100,
      maliyet: parseFloat(maliyet) || 0,
      description: description.trim() || null,
    }

    try {
      let saved: DemoProduct
      if (initialProduct) {
        saved = await api.updateDemoProduct(initialProduct.id, payload)
      } else {
        saved = await api.createDemoProduct(payload)
      }
      await onSuccess(saved)
    } catch (err: any) {
      setError(err.message || 'Kayıt sırasında bir hata oluştu.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal-card" style={{ maxWidth: '600px', width: '90%' }}>
        <h3>{initialProduct ? 'Demo Ürün Düzenle' : 'Yeni Demo Ürün Ekle'}</h3>
        <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '1.25rem' }}>
          Demo ürünler ürün koduna ihtiyaç duymaz. Bilgileri girip doğrudan reçete ve fiyat simülasyonu yapabilirsiniz.
        </p>

        {error && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Demo Ürün Adı *</label>
            <input
              required
              autoFocus
              placeholder="Örn: Prototip Akıllı Sayaç Modülü"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Kategori</label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">Kategori Seçin</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Birim *</label>
              <select value={unit} onChange={(e) => setUnit(e.target.value)}>
                <option value="Adet">Adet</option>
                <option value="Kg">Kg</option>
                <option value="Lt">Lt</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Reçete Çıktı Miktarı (n) *</label>
              <input
                type="number"
                step="any"
                min="0.0001"
                required
                placeholder="1.0000"
                value={recipeOutputQuantity}
                onChange={(e) => setRecipeOutputQuantity(e.target.value)}
              />
              <span className="helper-text">Reçete bileşenleri bu miktara ({unit}) göre girilir.</span>
            </div>

            <div className="form-group">
              <label>Taban Montaj / İşçilik Maliyeti (TL) *</label>
              <input
                type="number"
                step="0.01"
                required
                placeholder="0.00"
                value={maliyet}
                onChange={(e) => setMaliyet(e.target.value)}
              />
              <span className="helper-text">Reçete harici doğrudan taban montaj maliyeti.</span>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>KDV Oranı (%) *</label>
              <input
                type="number"
                step="1"
                required
                placeholder="20"
                value={kdvRate}
                onChange={(e) => setKdvRate(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Açıklama</label>
            <textarea
              rows={2}
              placeholder="Prototip veya deneme notları…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              İptal
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Kaydediliyor...' : initialProduct ? 'Güncelle' : 'Demo Ürünü Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
